import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import useAuth from '../../../hooks/useAuth';
import axios from '../../../utils/axios';
import { isMaster, perfilLabel } from '../../../utils/perfil';
import { AvatarIniciais, Num } from '../../../components/gps';
import { AreaArquivo, BotaoPrimario, BotaoSecundario, Campo, IconeOlho, classesEntrada } from '../../../components/gps/Modal';
import { HistoricoGPS } from '../../../components/historicoGPS';
import { cn } from '../../../lib/utils';

/* ============================================================================
   Meu perfil.

   Tela nova (19/08/2026). Reune o que estava espalhado ou inacessivel:

   - a FOTO da pessoa, que nao existia em lugar nenhum. Guardada como arquivo no
     servidor com o id do usuario no nome - `TAB_USUARIO` nao tem coluna de foto, e
     acrescentar coluna num SQL Server com dado real nao e algo para fazer de
     passagem. Ver o comentario extenso em users.controller.ts.

   - CR e REGIONAIS do proprio usuario. Antes so davam para ver abrindo o proprio
     cadastro na tela de Usuarios, que exige perfil de administrador - ou seja, um
     usuario comum nao tinha como consultar a que regionais ele tem acesso, que e
     exatamente o que explica por que ele ve uns dispositivos e nao outros.

   - TROCAR A PROPRIA SENHA. O endpoint `POST /users/trocasenha/:codigo` existe no
     backend desde sempre e NAO TINHA NENHUM CHAMADOR no front: a unica forma de
     trocar senha era pelo fluxo de "esqueci minha senha", com codigo por e-mail.
     (Ao ligar este botao encontrei uma escalacao de privilegio naquela rota; esta
     corrigida no service, com o registro do que era.)

   - HISTORICO, SO PARA MASTER. E a regra que o usuario pediu, e ela tem sentido:
     o historico de um usuario mistura ação de cadastro com login, IP e horario -
     e material de auditoria. Quem audita e o Master.

   QUEM VE O QUE: a tela é sempre a do PROPRIO usuario logado. Nao existe
   `/perfil/:id` de propósito - id na URL vira convite a trocar o numero e ler o
   perfil de outra pessoa. Os dados vem de `GET /users/eu`, que le o id do token.
   ============================================================================ */

type Regional = { codigo: number; nome: string };

type Eu = {
  codigo?: number;
  nome?: string;
  email?: string;
  cpf?: number | string;
  perfil?: string | number;
  status?: string | number;
  dataUltimaSenha?: string;
  createdAt?: string;
  cr?: { codigo?: number; nome?: string; descricao?: string } | null;
  usuarioRegionais?: Regional[];
};

/** Data em pt-BR; devolve '—' para vazio e para data invalida. */
function dataLegivel(valor?: string) {
  if (!valor) return '—';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** CPF só formatado quando tem os 11 digitos - senao mostra como veio. */
function cpfLegivel(valor?: number | string) {
  const so = String(valor ?? '').replace(/\D/g, '');
  if (so.length !== 11) return so || '—';
  return `${so.slice(0, 3)}.${so.slice(3, 6)}.${so.slice(6, 9)}-${so.slice(9)}`;
}

/**
 * Rotulo do perfil aceitando as DUAS formas que a API usa.
 *
 * `GET /users/eu` (que e o `findById` do service) devolve `perfil: Role[perfil]`,
 * ou seja a STRING 'MASTER' - enquanto o token e as listagens trazem o NUMERO 3.
 * `perfilLabel` do utils recebe numero e faz `Number(perfil)`: com 'MASTER' isso e
 * NaN e cai no `default`, entao a tela mostraria "-" para quem e Master.
 */
function rotuloPerfil(valor?: string | number): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const texto = String(valor).trim();
  if (/^\d+$/.test(texto)) return perfilLabel(Number(texto));
  // 'MASTER' -> 'Master'
  return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line py-2 last:border-0">
      <span className="flex-none text-[12px] font-semibold text-muted-foreground">{rotulo}</span>
      <span className="min-w-0 text-right text-[12px] text-ink">{children}</span>
    </div>
  );
}

function Cartao({ titulo, descricao, children }: { titulo: string; descricao?: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
      {/* Faixa de titulo na cor da paleta, igual ao cabecalho das tabelas - o
          sistema inteiro passa a ter uma unica marca de "topo de bloco". */}
      <header className="border-b border-primary/20 bg-primary/10 px-4 py-2">
        <h2 className="text-[12px] font-bold uppercase tracking-[0.06em] text-primary">{titulo}</h2>
        {descricao && <p className="mt-0.5 text-[11px] text-muted-foreground">{descricao}</p>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

PerfilPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function PerfilPage() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const master = isMaster(user);

  const [eu, setEu] = useState<Eu>({});
  const [carregando, setCarregando] = useState(true);

  const [foto, setFoto] = useState<string | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviandoFoto, setEnviandoFoto] = useState(false);

  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [senhaConfirma, setSenhaConfirma] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [trocandoSenha, setTrocandoSenha] = useState(false);

  const [aba, setAba] = useState<'dados' | 'acesso' | 'seguranca' | 'historico'>('dados');

  const codigo = Number(eu.codigo || (user as any)?.codigo || (user as any)?.id || 0);
  const nomeExibido = eu.nome || (user as any)?.nome || '—';

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await axios.get<Eu>('/users/eu');
      setEu(r.data || {});
    } catch (e: any) {
      /* CAMINHO ALTERNATIVO quando a API nao tem `/users/eu`.
         Aconteceu na pratica: o front recarrega sozinho (Vite), a API nao - ela
         continuava servindo o build anterior, e a tela abria com todos os campos em
         "—" e um erro de arquivo nao encontrado (o 404 da API cai no servidor de
         estaticos, e a mensagem que chega ao cliente e um ENOENT com caminho de
         disco). O mesmo vale em producao se o front subir antes da API.

         `/users/byId/:codigo` existe desde sempre e devolve o MESMO objeto - o
         `findById` do service e o que `/users/eu` chama por dentro. A diferenca e
         que ali o codigo vai na URL; aqui ele vem do token que ja esta na sessao,
         entao nao ha como pedir o perfil de outra pessoa por este caminho. */
      const semRota = e?.response?.status === 404;
      const meuCodigo = Number((user as any)?.codigo || (user as any)?.id || 0);
      if (semRota && meuCodigo) {
        try {
          const alt = await axios.get<Eu>(`/users/byId/${meuCodigo}`);
          setEu(alt.data || {});
          return;
        } catch (e2: any) {
          enqueueSnackbar(e2?.response?.data?.message || 'Não foi possível carregar seu perfil.', {
            variant: 'error',
          });
          return;
        }
      }
      enqueueSnackbar(
        semRota
          ? 'A API em execução ainda não tem a rota do perfil — reinicie a API.'
          : e?.response?.data?.message || 'Não foi possível carregar seu perfil.',
        { variant: 'error' }
      );
    } finally {
      setCarregando(false);
    }
  }, [enqueueSnackbar, user]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // A foto vem em chamada separada: ela e base64 e pesa mais que o resto do
  // cadastro somado - misturar as duas faria o perfil inteiro esperar pela imagem.
  useEffect(() => {
    if (!codigo) return;
    let cancelado = false;
    axios
      .get<{ foto: string | null }>(`/users/foto/${codigo}`)
      .then((r) => {
        if (!cancelado) setFoto(r.data?.foto || null);
      })
      .catch(() => {
        // Ausencia de foto nao e erro que mereça aviso na tela: cai nas iniciais.
        if (!cancelado) setFoto(null);
      });
    return () => {
      cancelado = true;
    };
  }, [codigo]);

  const previa = useMemo(() => (arquivo ? URL.createObjectURL(arquivo) : null), [arquivo]);
  useEffect(() => {
    // `createObjectURL` sem `revokeObjectURL` deixa o blob preso na memoria da aba
    // a cada troca de arquivo.
    return () => {
      if (previa) URL.revokeObjectURL(previa);
    };
  }, [previa]);

  async function salvarFoto() {
    if (!arquivo) return;
    setEnviandoFoto(true);
    try {
      const corpo = new FormData();
      corpo.append('file', arquivo);
      await axios.post('/users/foto', corpo, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Salvou. A releitura e so para exibir: se ELA falhar, a foto foi salva
      // mesmo assim - antes caia no catch e a tela dizia "nao foi possivel salvar".
      enqueueSnackbar('Foto atualizada.', { variant: 'success' });
      setArquivo(null);
      try {
        const r = await axios.get<{ foto: string | null }>(`/users/foto/${codigo}`);
        setFoto(r.data?.foto || null);
      } catch {
        /* mantem a previa; a foto nova aparece na proxima abertura */
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível salvar a foto.', { variant: 'error' });
    } finally {
      setEnviandoFoto(false);
    }
  }

  async function removerFoto() {
    setEnviandoFoto(true);
    try {
      await axios.delete(`/users/foto/${codigo}`);
      setFoto(null);
      setArquivo(null);
      enqueueSnackbar('Foto removida.', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível remover a foto.', { variant: 'error' });
    } finally {
      setEnviandoFoto(false);
    }
  }

  const senhaOk = senhaNova.length >= 6 && senhaNova === senhaConfirma && senhaAtual.length > 0;

  async function trocarSenha() {
    if (!senhaOk) return;
    setTrocandoSenha(true);
    try {
      await axios.post(`/users/trocasenha/${codigo}`, { codigo, senhaAtual, senhaNova });
      setSenhaAtual('');
      setSenhaNova('');
      setSenhaConfirma('');
      enqueueSnackbar('Senha alterada.', { variant: 'success' });
      carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível alterar a senha.', { variant: 'error' });
    } finally {
      setTrocandoSenha(false);
    }
  }

  const regionais = eu.usuarioRegionais || [];
  /* MASTER TAMBEM TEM TODAS (29/09/2026).

     Testava so o vinculo com a regional 37. Acontece que "enxerga todas" tem
     DUAS fontes independentes, e basta uma: o vinculo, ou o perfil Master - a
     mesma regra que `acesso-regional.ts` centralizou no backend depois de o
     defeito aparecer em producao ("um usuario nao estava aparecendo as
     regionais dele mesmo, ele com usuario master").

     Aqui ele tinha sobrevivido: um Master SEM a linha em TAB_USUARIO_REGIONAL
     abria "Meu perfil" e lia "0 regional(is)" no cabecalho, com o cartao
     Regionais vazio - o sistema informando ao dono do sistema que ele nao tem
     acesso a nada.

     `master` ja existia tres linhas acima (via `isMaster`), usado para decidir
     a aba Historico. Era so usa-lo. */
  const temTodas = master || regionais.some((r) => Number(r.codigo) === 37);

  const abas = [
    { id: 'dados' as const, rotulo: 'Meus dados' },
    { id: 'acesso' as const, rotulo: 'CR e regionais' },
    { id: 'seguranca' as const, rotulo: 'Senha' },
    // O historico só entra na lista para Master - nao fica desabilitado. Aba
    // visivel e travada anuncia a existencia de algo que a pessoa nao pode ver.
    ...(master ? [{ id: 'historico' as const, rotulo: 'Histórico' }] : []),
  ];

  return (
    <Page title="Meu perfil - GPShub">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 py-5">
        <header className="mb-4 flex flex-wrap items-center gap-4">
          <div className="relative flex-none">
            {foto ? (
              <img
                src={foto}
                alt={`Foto de ${nomeExibido}`}
                className="h-16 w-16 rounded-xl border border-line object-cover"
              />
            ) : (
              <AvatarIniciais nome={nomeExibido} className="h-16 w-16 rounded-xl text-[22px]" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="block truncate text-[20px] font-extrabold tracking-[-0.02em]">{nomeExibido}</h1>
            <p className="block truncate text-[12px] text-muted-foreground">{eu.email || '—'}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex h-[19px] items-center rounded bg-primary/10 px-1.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                {rotuloPerfil(eu.perfil ?? (user as any)?.perfil)}
              </span>
              {eu.cr?.nome && (
                <span className="inline-flex h-[19px] items-center rounded bg-soft px-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  CR {eu.cr.nome}
                </span>
              )}
              <span className="text-[11px] text-faint">
                {temTodas ? 'acesso a todas as regionais' : `${regionais.length} regional(is)`}
              </span>
            </div>
          </div>
        </header>

        <div role="tablist" aria-label="Meu perfil" className="mb-4 flex gap-1 overflow-x-auto border-b border-line">
          {abas.map((a) => (
            <button
              key={a.id}
              type="button"
              role="tab"
              aria-selected={aba === a.id}
              onClick={() => setAba(a.id)}
              className={cn(
                '-mb-px flex-none border-b-2 px-3 py-2 text-[12px] transition-colors',
                aba === a.id
                  ? 'border-primary font-bold text-primary'
                  : 'border-transparent font-semibold text-muted-foreground hover:border-line hover:text-ink'
              )}
            >
              {a.rotulo}
            </button>
          ))}
        </div>

        {carregando ? (
          <div className="rounded-lg border border-primary/25 bg-surface p-6 text-center text-[12px] text-muted-foreground">
            Carregando seu perfil...
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {aba === 'dados' && (
              <>
                <Cartao titulo="Cadastro" descricao="Nome, e-mail e CPF são alterados pelo administrador.">
                  <Linha rotulo="Nome">{eu.nome || '—'}</Linha>
                  <Linha rotulo="E-mail">{eu.email || '—'}</Linha>
                  <Linha rotulo="CPF">
                    <Num>{cpfLegivel(eu.cpf)}</Num>
                  </Linha>
                  <Linha rotulo="Perfil">{rotuloPerfil(eu.perfil)}</Linha>
                  <Linha rotulo="Status">{String(eu.status ?? '—')}</Linha>
                  <Linha rotulo="Cadastrado em">{dataLegivel(eu.createdAt)}</Linha>
                  <Linha rotulo="Última troca de senha">{dataLegivel(eu.dataUltimaSenha)}</Linha>
                </Cartao>

                <Cartao titulo="Foto" descricao="PNG, JPG ou WEBP, até 512 kB. Aparece no menu da conta.">
                  <div className="flex items-start gap-4">
                    <div className="flex-none">
                      {previa || foto ? (
                        <img
                          src={previa || foto || ''}
                          alt="Prévia da foto"
                          className="h-24 w-24 rounded-xl border border-line object-cover"
                        />
                      ) : (
                        <AvatarIniciais nome={nomeExibido} className="h-24 w-24 rounded-xl text-[30px]" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <AreaArquivo
                        arquivo={arquivo}
                        onArquivo={setArquivo}
                        accept="image/png,image/jpeg,image/webp"
                        extensoes={['.png', '.jpg', '.jpeg', '.webp']}
                        rotulo="Arraste a foto aqui ou clique para escolher"
                        dica="Quadrada fica melhor"
                        disabled={enviandoFoto}
                      />

                      <div className="mt-3 flex flex-wrap gap-2">
                        <BotaoPrimario onClick={salvarFoto} disabled={!arquivo || enviandoFoto}>
                          {enviandoFoto ? 'Enviando...' : 'Salvar foto'}
                        </BotaoPrimario>
                        {foto && (
                          <BotaoSecundario onClick={removerFoto} disabled={enviandoFoto}>
                            Remover foto
                          </BotaoSecundario>
                        )}
                      </div>
                    </div>
                  </div>
                </Cartao>
              </>
            )}

            {aba === 'acesso' && (
              <>
                <Cartao titulo="Centro de resultado (CR)">
                  {eu.cr?.codigo ? (
                    <>
                      <Linha rotulo="Código">
                        <Num>{eu.cr.codigo}</Num>
                      </Linha>
                      <Linha rotulo="Nome">{eu.cr.nome || '—'}</Linha>
                      {eu.cr.descricao && <Linha rotulo="Descrição">{eu.cr.descricao}</Linha>}
                    </>
                  ) : (
                    <p className="text-[12px] text-muted-foreground">
                      Nenhum CR vinculado ao seu usuário. Quem vincula é o administrador, na tela de Usuários.
                    </p>
                  )}
                </Cartao>

                <Cartao
                  titulo="Regionais"
                  descricao="É esta lista que decide quais dispositivos e grupos você vê no sistema."
                >
                  {temTodas ? (
                    <p className="rounded-lg border border-ok bg-ok-soft p-3 text-[12px] text-ink">
                      Seu usuário tem acesso a <strong>todas as regionais</strong>.
                    </p>
                  ) : regionais.length === 0 ? (
                    <p className="text-[12px] text-muted-foreground">
                      Nenhuma regional vinculada — por isso as listas aparecem vazias. Peça acesso ao
                      administrador da sua regional.
                    </p>
                  ) : (
                    <ul className="flex flex-wrap gap-1.5">
                      {regionais.map((r) => (
                        <li
                          key={r.codigo}
                          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-soft px-2 py-1 text-[12px]"
                        >
                          <Num className="text-faint">{r.codigo}</Num>
                          {r.nome}
                        </li>
                      ))}
                    </ul>
                  )}
                </Cartao>
              </>
            )}

            {aba === 'seguranca' && (
              <Cartao
                titulo="Trocar senha"
                descricao="Pede a senha atual. A nova precisa de 6 caracteres ou mais."
              >
                <div className="max-w-[420px] space-y-3">
                  <Campo rotulo="Senha atual" obrigatorio>
                    <div className="relative">
                      <input
                        type={mostrarSenha ? 'text' : 'password'}
                        value={senhaAtual}
                        onChange={(e) => setSenhaAtual(e.target.value)}
                        autoComplete="current-password"
                        className={cn(classesEntrada, 'pr-10')}
                      />
                      <button
                        type="button"
                        onClick={() => setMostrarSenha((v) => !v)}
                        aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                        aria-pressed={mostrarSenha}
                        className="absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:bg-soft hover:text-ink"
                      >
                        <IconeOlho fechado={mostrarSenha} />
                      </button>
                    </div>
                  </Campo>

                  <Campo rotulo="Senha nova" obrigatorio>
                    <input
                      type={mostrarSenha ? 'text' : 'password'}
                      value={senhaNova}
                      onChange={(e) => setSenhaNova(e.target.value)}
                      autoComplete="new-password"
                      className={cn(classesEntrada, senhaNova.length > 0 && senhaNova.length < 6 && 'border-warn')}
                    />
                    {senhaNova.length > 0 && senhaNova.length < 6 && (
                      <span className="mt-1 block text-[11px] text-warn">Faltam {6 - senhaNova.length} caracteres.</span>
                    )}
                  </Campo>

                  <Campo rotulo="Confirmar senha nova" obrigatorio>
                    <input
                      type={mostrarSenha ? 'text' : 'password'}
                      value={senhaConfirma}
                      onChange={(e) => setSenhaConfirma(e.target.value)}
                      autoComplete="new-password"
                      className={cn(
                        classesEntrada,
                        senhaConfirma.length > 0 && senhaConfirma !== senhaNova && 'border-danger'
                      )}
                    />
                    {senhaConfirma.length > 0 && senhaConfirma !== senhaNova && (
                      <span className="mt-1 block text-[11px] text-danger">As duas senhas não são iguais.</span>
                    )}
                  </Campo>

                  <BotaoPrimario onClick={trocarSenha} disabled={!senhaOk || trocandoSenha}>
                    {trocandoSenha ? 'Alterando...' : 'Alterar senha'}
                  </BotaoPrimario>
                </div>
              </Cartao>
            )}

            {aba === 'historico' && master && (
              <div className="lg:col-span-2">
                <Cartao
                  titulo="Histórico do meu usuário"
                  descricao="Visível apenas para perfil Master: registra alteração de cadastro, login, IP e horário."
                >
                  {codigo ? (
                    <HistoricoGPS tipo={'USUARIO'} chave={String(codigo)} />
                  ) : (
                    <p className="text-[12px] text-muted-foreground">Sem código de usuário para consultar.</p>
                  )}
                </Cartao>
              </div>
            )}
          </div>
        )}
      </div>
    </Page>
  );
}
